import { formatDateLocalized, toDate } from "../../utils/date";
import useLocale from "../../hooks/useLocale";
import {
	Utensils,
	Home,
	HeartPulse,
	User,
	Euro,
	Circle,
	FileText,
	CalendarSync,
	PiggyBank,
} from "lucide-react";
import { memo, useMemo } from "react";

// Category icons mapping
const categoryIcons = {
	food: <Utensils size={16} />,
	housing: <Home size={16} />,
	health: <HeartPulse size={16} />,
	personal: <User size={16} />,
	savings: <PiggyBank size={18} />,
	income: <Euro size={16} />,
	general: <FileText size={16} />,
	other: <Circle size={16} />,
};

// Function to format the currency - memorized
const formatCurrency = (amount, language) => {
	return new Intl.NumberFormat(language === "fr" ? "fr-FR" : "en-GB", {
		style: "currency",
		currency: "EUR",
		minimumFractionDigits: 2,
	}).format(amount);
};

const EntryCard = ({
	entry,
	translate,
	onSelect,
	showDate = true,
	showCategory = true,
}) => {
	const { locale } = useLocale();
	const language = locale.startsWith("fr") ? "fr" : "en";

	// Memoize derived values to avoid recalculations
	const memoizedData = useMemo(() => {
		const { title, amount, date, category, subCategory, isFromRecurring } =
			entry;

		// Get category icon
		const CategoryIcon =
			categoryIcons[category?.toLowerCase()] || categoryIcons.other;

		// Get translated category label
		const categoryLabel = translate(`category.${category}`);

		// Get translated subcategory label
		const subCategoryLabel = subCategory
			? translate(`subCategory.${category}.${subCategory}`) ||
			  subCategory.charAt(0).toUpperCase() + subCategory.slice(1)
			: null;

		// Format currency
		const formattedAmount = formatCurrency(amount, language);

		// Format date
		const formattedDate = showDate
			? formatDateLocalized(toDate(date), locale)
			: null;

		return {
			title,
			CategoryIcon,
			categoryLabel,
			subCategoryLabel,
			formattedAmount,
			formattedDate,
			isFromRecurring,
			category,
		};
	}, [entry, translate, language, locale, showDate]);

	return (
		<div
			className={`entry-card category-${memoizedData.category}`}
			onClick={onSelect}
		>
			<div className="entry-main">
				<h5>
					{memoizedData.isFromRecurring && <CalendarSync size={14} />}
					{memoizedData.title}
				</h5>

				<div className="entry-info">
					{showCategory && entry.category && (
						<span>
							{memoizedData.CategoryIcon}
							{memoizedData.categoryLabel}
						</span>
					)}
					{memoizedData.subCategoryLabel && (
						<span>{memoizedData.subCategoryLabel}</span>
					)}
				</div>
			</div>

			<div className="entry-amount">
				{memoizedData.formattedAmount}
				{memoizedData.formattedDate && (
					<span className="entry-date">{memoizedData.formattedDate}</span>
				)}
			</div>
		</div>
	);
};

// Memoization with a more precise custom comparison
export default memo(EntryCard, (prevProps, nextProps) => {
	// Specific checks to avoid unnecessary re-renders
	return (
		prevProps.entry.id === nextProps.entry.id &&
		prevProps.entry.title === nextProps.entry.title &&
		prevProps.entry.amount === nextProps.entry.amount &&
		prevProps.entry.category === nextProps.entry.category &&
		prevProps.entry.subCategory === nextProps.entry.subCategory &&
		prevProps.showDate === nextProps.showDate &&
		prevProps.showCategory === nextProps.showCategory &&
		// Check if onSelect has changed (pointer equality)
		prevProps.onSelect === nextProps.onSelect
	);
});
